import React, { useState, useMemo, useEffect } from "react";
import PageHeading from "../utils/PageHeading";
import {
  Container,
  Typography,
  Box,
  TextField,
  InputAdornment,
  Chip,
  useMediaQuery,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import { useNavigate } from "react-router-dom";
import { getAllDrugs } from "../services/drugDataService";

/* ── Animation keyframes injected once ── */
if (typeof document !== "undefined") {
  const id = "medicines-anim";
  if (!document.getElementById(id)) {
    const s = document.createElement("style");
    s.id = id;
    s.textContent = `
      @keyframes fadeSlideIn {
        from { opacity: 0; transform: translateY(16px); }
        to   { opacity: 1; transform: translateY(0); }
      }
      @keyframes shimmerRow {
        0%   { background-position: -400px 0; }
        100% { background-position: 400px 0; }
      }
      .med-row {
        animation: fadeSlideIn 0.4s ease both;
        transition: background 0.2s, box-shadow 0.2s;
        cursor: pointer;
      }
      .med-row:hover {
        background: #f0fdfa !important;
        box-shadow: inset 4px 0 0 #43C5B9;
      }
    `;
    document.head.appendChild(s);
  }
}

/* ── Helpers ── */
const normalizeDosage = (str) => {
  if (!str) return "";
  let s = str.toLowerCase();
  const mixMatch = s.match(/mix\s*\d+\s*\((mix\s*\d+\/\d+)\)/);
  if (mixMatch) s = mixMatch[1];
  s = s.replace(/\biu\b/g, "units");
  return s.replace(/\s*\([^)]*\)/g, "").trim();
};

const getRegionAvgPrice = (drug, regionKeyword, dosage) => {
  if (!drug || !dosage) return null;
  let total = 0;
  let count = 0;
  const region = drug.regions?.find((r) =>
    r?.region_name?.toLowerCase().includes(regionKeyword.toLowerCase())
  );
  if (!region) return null;
  region.pharmacies?.forEach((p) =>
    p.dosages?.forEach((d) => {
      if (
        normalizeDosage(d.dosage_strength) === normalizeDosage(dosage) &&
        d.price_cash_usd != null
      ) {
        total += parseFloat(d.price_cash_usd);
        count++;
      }
    })
  );
  return count > 0 ? total / count : null;
};

const getFirstDosage = (drug) => {
  for (const r of drug?.regions || []) {
    for (const p of r.pharmacies || []) {
      if (p.dosages?.[0]?.dosage_strength) return p.dosages[0].dosage_strength;
    }
  }
  return null;
};

const fmt = (v) =>
  v != null
    ? `$${v.toLocaleString("en-US", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })}`
    : "—";

const pctHigher = (usa, other) => {
  if (!usa || !other || other === 0) return null;
  const diff = ((usa - other) / other) * 100;
  return diff;
};

/* ── Main component ── */
export default function Medicines() {
  const navigate = useNavigate();
  const [search, setSearch] = useState("");
  const [sortField, setSortField] = useState("brand");
  const [sortDir, setSortDir] = useState("asc");
  const [rawDrugs, setRawDrugs] = useState([]);
  const isMobile = useMediaQuery("(max-width:768px)");

  /* Fetch drug data (backend-first, JSON fallback) */
  useEffect(() => {
    getAllDrugs().then(setRawDrugs).catch(() => setRawDrugs([]));
  }, []);

  /* Build table rows once raw data is available */
  const rows = useMemo(() => {
    return rawDrugs
      .map((drug) => {
        const meta = drug.drug_metadata || {};
        const dosage = getFirstDosage(drug);
        const usaAvg = getRegionAvgPrice(drug, "usa", dosage);
        const euAvg = getRegionAvgPrice(drug, "europe", dosage);
        const asiaAvg = getRegionAvgPrice(drug, "asia", dosage);

        // Best non-USA price (lowest available)
        const nonUsa = [euAvg, asiaAvg].filter((v) => v != null);
        const lowestGlobal = nonUsa.length > 0 ? Math.min(...nonUsa) : null;

        const pct = pctHigher(usaAvg, lowestGlobal);

        return {
          brand: meta.brand_name || "—",
          generic: meta.generic_name || "—",
          drug_class: meta.drug_class || "—",
          usaAvg,
          euAvg,
          asiaAvg,
          lowestGlobal,
          pct,
          dosage,
        };
      })
      .filter(Boolean);
  }, [rawDrugs]);

  /* Filter */
  const filtered = useMemo(() => {
    const q = search.toLowerCase();
    return rows.filter(
      (r) =>
        r.brand.toLowerCase().includes(q) ||
        r.generic.toLowerCase().includes(q) ||
        r.drug_class.toLowerCase().includes(q)
    );
  }, [rows, search]);

  /* Sort */
  const sorted = useMemo(() => {
    return [...filtered].sort((a, b) => {
      let valA = a[sortField];
      let valB = b[sortField];
      if (typeof valA === "string") valA = valA.toLowerCase();
      if (typeof valB === "string") valB = valB.toLowerCase();
      if (valA == null) return 1;
      if (valB == null) return -1;
      if (valA < valB) return sortDir === "asc" ? -1 : 1;
      if (valA > valB) return sortDir === "asc" ? 1 : -1;
      return 0;
    });
  }, [filtered, sortField, sortDir]);

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortField(field);
      setSortDir("asc");
    }
  };

  const SortIcon = ({ field }) => {
    if (sortField !== field)
      return (
        <ArrowUpwardIcon
          sx={{ fontSize: 13, opacity: 0.3, ml: 0.5, verticalAlign: "middle" }}
        />
      );
    return sortDir === "asc" ? (
      <ArrowUpwardIcon
        sx={{ fontSize: 13, color: "#43C5B9", ml: 0.5, verticalAlign: "middle" }}
      />
    ) : (
      <ArrowDownwardIcon
        sx={{ fontSize: 13, color: "#43C5B9", ml: 0.5, verticalAlign: "middle" }}
      />
    );
  };

  const thStyle = {
    padding: isMobile ? "10px 8px" : "14px 16px",
    textAlign: "left",
    fontSize: isMobile ? "0.7rem" : "0.78rem",
    fontWeight: 700,
    color: "#4a5568",
    letterSpacing: "0.06em",
    textTransform: "uppercase",
    borderBottom: "2px solid #e2e8f0",
    cursor: "pointer",
    userSelect: "none",
    whiteSpace: "nowrap",
    backgroundColor: "#f8fafc",
  };

  const tdStyle = (align = "left") => ({
    padding: isMobile ? "10px 8px" : "14px 16px",
    textAlign: align,
    fontSize: isMobile ? "0.75rem" : "0.85rem",
    color: "#2d3748",
    borderBottom: "1px solid #edf2f7",
    verticalAlign: "middle",
  });

  return (
    <div className="about_page body_bg">
      <div className="about_banner_main">
        <PageHeading
          title={"Medicines"}
          pageTitle={"Drug Price Comparison"}
        />
      </div>

      <Container maxWidth="xl" sx={{ py: { xs: 3, md: 5 } }}>
        {/* ── Header row ── */}
        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "column", sm: "row" },
            alignItems: { xs: "flex-start", sm: "center" },
            justifyContent: "space-between",
            gap: 2,
            mb: 3,
          }}
        >
          <Box>
            <Typography
              variant="h5"
              sx={{
                fontWeight: 800,
                fontSize: { xs: "1.2rem", md: "1.5rem" },
                color: "#1a202c",
              }}
            >
              Drug Price Comparison
            </Typography>
            <Typography
              variant="body2"
              sx={{ color: "#718096", mt: 0.5, fontSize: "0.85rem" }}
            >
              Compare U.S. prescription drug prices against European and Asian
              averages.
            </Typography>
          </Box>

          <Box sx={{ display: "flex", gap: 1.5, alignItems: "center", flexWrap: "wrap" }}>
            <Chip
              label={`${sorted.length} drug${sorted.length !== 1 ? "s" : ""}`}
              size="small"
              sx={{
                backgroundColor: "#e6fffa",
                color: "#234e52",
                fontWeight: 700,
                fontSize: "0.75rem",
              }}
            />
            <TextField
              size="small"
              placeholder="Search drugs…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ fontSize: 18, color: "#a0aec0" }} />
                  </InputAdornment>
                ),
              }}
              sx={{
                width: { xs: "100%", sm: 260 },
                "& .MuiOutlinedInput-root": {
                  borderRadius: "10px",
                  backgroundColor: "white",
                  fontSize: "0.85rem",
                },
              }}
            />
          </Box>
        </Box>

        {/* ── Legend ── */}
        <Box
          sx={{
            display: "flex",
            gap: 2,
            mb: 2,
            flexWrap: "wrap",
            alignItems: "center",
          }}
        >
          {[
            { color: "#e74c3c", label: "USA price (highest)" },
            { color: "#27ae60", label: "Lowest available" },
            { color: "#F5A623", label: "Mid-range" },
          ].map(({ color, label }) => (
            <Box
              key={label}
              sx={{ display: "flex", alignItems: "center", gap: 0.8 }}
            >
              <Box
                sx={{
                  width: 10,
                  height: 10,
                  borderRadius: "50%",
                  backgroundColor: color,
                }}
              />
              <Typography sx={{ fontSize: "0.75rem", color: "#718096" }}>
                {label}
              </Typography>
            </Box>
          ))}
        </Box>

        {/* ── Table wrapper ── */}
        <Box
          sx={{
            borderRadius: "16px",
            overflow: "hidden",
            boxShadow: "0 4px 24px rgba(0,0,0,0.07)",
            border: "1px solid #e2e8f0",
            overflowX: "auto",
            WebkitOverflowScrolling: "touch",
          }}
        >
          <table
            style={{
              width: "100%",
              borderCollapse: "collapse",
              minWidth: isMobile ? "600px" : "900px",
            }}
          >
            <thead>
              <tr>
                {[
                  { label: "Drug (Brand)", field: "brand" },
                  { label: "Generic Name", field: "generic", hide: isMobile },
                  { label: "Class", field: "drug_class", hide: isMobile },
                  { label: "USA Avg.", field: "usaAvg" },
                  { label: "Europe Avg.", field: "euAvg" },
                  { label: "Asia Avg.", field: "asiaAvg" },
                  { label: "USA vs. Global", field: "pct" },
                ].map(
                  ({ label, field, hide }) =>
                    !hide && (
                      <th
                        key={field}
                        style={thStyle}
                        onClick={() => handleSort(field)}
                      >
                        {label}
                        <SortIcon field={field} />
                      </th>
                    )
                )}
              </tr>
            </thead>

            <tbody>
              {sorted.length === 0 && (
                <tr>
                  <td
                    colSpan={7}
                    style={{
                      ...tdStyle("center"),
                      padding: "48px",
                      color: "#a0aec0",
                    }}
                  >
                    No drugs found for "{search}"
                  </td>
                </tr>
              )}
              {sorted.map((row, idx) => {
                const prices = [row.usaAvg, row.euAvg, row.asiaAvg].filter(
                  (v) => v != null
                );
                const minP = prices.length ? Math.min(...prices) : null;
                const maxP = prices.length ? Math.max(...prices) : null;

                const priceColor = (val) => {
                  if (val == null) return "#a0aec0";
                  if (val === maxP && val === minP) return "#2d3748";
                  if (val === maxP) return "#e74c3c";
                  if (val === minP) return "#27ae60";
                  return "#F5A623";
                };

                const pctVal = row.pct;
                const pctLabel =
                  pctVal != null
                    ? pctVal > 0
                      ? `+${pctVal.toFixed(1)}%`
                      : `${pctVal.toFixed(1)}%`
                    : "—";
                const pctColor =
                  pctVal == null ? "#a0aec0" : pctVal > 0 ? "#e74c3c" : "#27ae60";

                return (
                  <tr
                    key={row.brand + idx}
                    className="med-row"
                    style={{
                      animationDelay: `${Math.min(idx * 0.03, 0.6)}s`,
                      backgroundColor: idx % 2 === 0 ? "#ffffff" : "#fafafa",
                    }}
                    onClick={() =>
                      navigate(
                        `/price-compare?medicine=${encodeURIComponent(row.brand)}`
                      )
                    }
                  >
                    {/* Brand */}
                    <td style={tdStyle()}>
                      <Box>
                        <Typography
                          sx={{
                            fontWeight: 700,
                            fontSize: isMobile ? "0.78rem" : "0.88rem",
                            color: "#1a202c",
                            lineHeight: 1.2,
                          }}
                        >
                          {row.brand}
                        </Typography>
                        {isMobile && (
                          <Typography
                            sx={{ fontSize: "0.7rem", color: "#718096", mt: 0.2 }}
                          >
                            {row.generic}
                          </Typography>
                        )}
                      </Box>
                    </td>

                    {/* Generic — hidden on mobile */}
                    {!isMobile && (
                      <td style={tdStyle()}>
                        <Typography
                          sx={{ fontSize: "0.82rem", color: "#4a5568" }}
                        >
                          {row.generic}
                        </Typography>
                      </td>
                    )}

                    {/* Class — hidden on mobile */}
                    {!isMobile && (
                      <td style={tdStyle()}>
                        <Box
                          sx={{
                            display: "inline-block",
                            backgroundColor: "#edf2f7",
                            borderRadius: "6px",
                            px: 1,
                            py: 0.2,
                          }}
                        >
                          <Typography
                            sx={{
                              fontSize: "0.72rem",
                              color: "#4a5568",
                              fontWeight: 600,
                            }}
                          >
                            {row.drug_class}
                          </Typography>
                        </Box>
                      </td>
                    )}

                    {/* USA */}
                    <td style={{ ...tdStyle("right") }}>
                      <Typography
                        sx={{
                          fontWeight: 700,
                          fontSize: isMobile ? "0.78rem" : "0.88rem",
                          color: priceColor(row.usaAvg),
                        }}
                      >
                        {fmt(row.usaAvg)}
                      </Typography>
                    </td>

                    {/* Europe */}
                    <td style={tdStyle("right")}>
                      <Typography
                        sx={{
                          fontWeight: 600,
                          fontSize: isMobile ? "0.78rem" : "0.88rem",
                          color: priceColor(row.euAvg),
                        }}
                      >
                        {fmt(row.euAvg)}
                      </Typography>
                    </td>

                    {/* Asia */}
                    <td style={tdStyle("right")}>
                      <Typography
                        sx={{
                          fontWeight: 600,
                          fontSize: isMobile ? "0.78rem" : "0.88rem",
                          color: priceColor(row.asiaAvg),
                        }}
                      >
                        {fmt(row.asiaAvg)}
                      </Typography>
                    </td>

                    {/* % Difference */}
                    <td style={tdStyle("center")}>
                      {pctVal != null ? (
                        <Box
                          sx={{
                            display: "inline-flex",
                            alignItems: "center",
                            gap: 0.4,
                            backgroundColor:
                              pctVal > 0 ? "#fff5f5" : "#f0fdf4",
                            borderRadius: "8px",
                            px: 1.2,
                            py: 0.4,
                          }}
                        >
                          {pctVal > 0 ? (
                            <TrendingUpIcon
                              sx={{ fontSize: 13, color: pctColor }}
                            />
                          ) : (
                            <ArrowDownwardIcon
                              sx={{ fontSize: 13, color: pctColor }}
                            />
                          )}
                          <Typography
                            sx={{
                              fontSize: "0.75rem",
                              fontWeight: 700,
                              color: pctColor,
                            }}
                          >
                            {pctLabel}
                          </Typography>
                        </Box>
                      ) : (
                        <Typography
                          sx={{ fontSize: "0.75rem", color: "#a0aec0" }}
                        >
                          —
                        </Typography>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </Box>

        {/* ── Footer note ── */}
        <Box sx={{ mt: 2, px: 1 }}>
          <Typography sx={{ fontSize: "0.72rem", color: "#a0aec0", lineHeight: 1.6 }}>
            Prices shown are averages of cash prices collected from publicly
            available pharmacy data. Click any row to see detailed pharmacy-level
            pricing. "USA vs. Global" shows how much higher the average U.S.
            price is compared to the lowest international price available.
          </Typography>
        </Box>
      </Container>
    </div>
  );
}
